import { AfterViewInit, Component, ElementRef, Inject, Input, OnDestroy, OnInit, Renderer2, signal } from '@angular/core';
import { get, isUndefined } from 'lodash';
import { BehaviorSubject, combineLatest, concat, fromEvent, merge, of, range, Subject } from 'rxjs';
import { concatMap, delay, map, repeat, switchMap, takeLast, takeUntil, tap } from 'rxjs/operators';

import { DebugLogService } from '@finder/shared/services/debug-log/debug-log.service';
import { CommonModule, DOCUMENT } from '@angular/common';
import { CarouselLabels, SlideAnnouncementData } from './carousel.interface';
import { ComponentHeaderComponent } from '../component-header/component-header.component';
import { CarouselContentRendererComponent } from '../carousel-content-renderer/carousel-content-renderer.component';
import { FontIconComponent } from '../font-icon/font-icon.component';
import { SlideAnnouncementPipe } from './slide-announcement.pipe';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { SsrService } from '@finder/shared/services/ssr/ssr.service';

/**
 * This component does not get along with SSR because the timers never let
 * the rendering script finish. It's best to just let then render in the
 * browser or bail before letting the component subscribe to the timers.
 */

@Component({
    selector: 'finder-carousel',
    templateUrl: './carousel.component.html',
    styleUrls: ['./carousel.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ComponentHeaderComponent,
        CarouselContentRendererComponent,
        FontIconComponent,
        SlideAnnouncementPipe,
    ],
})
export class CarouselComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() data: any;

    items;
    currentIndex = 0;
    prevArrow: string;
    nextArrow: string;
    externalNav = false; // TODO - get this from content
    labels: CarouselLabels;
    hasHeader = false;
    labelsSig = signal<any>({});

    protected slideAnnouncementData: SlideAnnouncementData;
    private destroy$ = new Subject<void>();
    private cancelDelay$ = new Subject<void>();
    private selectedIndex = new BehaviorSubject<number>(void 0);
    private itemCount: number;
    private selectedIndexValue;

    constructor(
        private el: ElementRef,
        private debugLog: DebugLogService,
        private renderer: Renderer2,
        private winRef: WindowRef,
        private ssrService: SsrService,
        @Inject(DOCUMENT) private document: Document,
    ) { }

    ngOnInit(): void {
        this.hasHeader = Object.keys(this.data?.headerData || {}).length > 0;
    }

    ngAfterViewInit(): void {
        this.setActiveClass(0);
        this.setNavContent();

        this.labelsSig.set(this.labels);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    setActive(itemIndex: number) {
        // Get a valid index
        itemIndex = this.getValidIndex(itemIndex);

        this.currentIndex = itemIndex;
        this.selectedIndex.next(itemIndex);
        this.setActiveClass(itemIndex);
    }

    private setNavContent() {
        const document = this.document;

        // Get the number of blocks
        this.itemCount = get(this.data, `blockData`, []).length;

        // Turn the number of items into a simple count array (e.g., 3 items = [ 1, 2, 3 ])
        // This is used to create the "nav dots" element
        this.items = [...new Array(this.itemCount).keys()];

        // If we don't find any blocks hide this component
        if (this.itemCount === 0) {
            return;
        }

        // Touch
        const swipe$ = this.getTouch(document);

        // Arrow icons
        this.prevArrow = get(this.data, 'prevArrow', 'previous-2');
        this.nextArrow = get(this.data, 'nextArrow', 'next-2');

        // Labels
        this.labels = this.data?.labels;

        // Get carousel
        const carousel$ = this.getCarousel();

        // @SSR: If we're rendering on the server don't subscribe to the subjects
        if (this.ssrService.isSSR()) {
            return;
        }

        merge(carousel$, swipe$)
            .pipe(takeUntil(this.destroy$))
            .subscribe();
    }

    private getCarousel() {
        // Set up the range based on the number of items and loop thru them on a timer
        const currentIndex$ = range(0, this.itemCount)
            .pipe(
                concatMap(idx => {
                    const shouldAutoAdvance = this.getAutoAdvance();

                    if (!shouldAutoAdvance) {
                        return of(this.currentIndex);
                    }

                    // Get the timer
                    // The conditional looks at a selected value - if we have one, then we want to make sure the
                    // current index matches. If it doesn't the timer will be 0 and this will allow us to speed
                    // past the range index until we align with the selected one. This way we can keep in step
                    // with the normal carousel rotation.
                    // For example, if we are on item index 2 (of 3 items total) and we select index 1 we want
                    // to skip past index 0 to get to 1, then the selected item choice will expire and the next
                    // loop the index will be 2 and normal carousel rotation will occur. This results in taking a
                    // little longer than the timer value for any given carousel item but that is okay because
                    // if a guest makes a selection they probably want to see it anyway, so lingering a little
                    // longer is fine.
                    const timer = this.currentIndexSetup(idx,
                        !isUndefined(this.selectedIndexValue) ? idx === this.selectedIndexValue : true);

                    this.debugLog.log(`***** currentIndex$ - post`, idx, this.currentIndex, timer);

                    // This is intended to delay the appropriate amount for each component with
                    // the ability to cancel the delay (when there is interaction with the carousel)
                    return of(this.currentIndex).pipe(
                        takeUntil(this.cancelDelay$),
                        delay(timer)
                    );
                }),
                repeat()
            );

        // Setup "dot" click handler and how long it takes before the timer starts back up
        const selectedIndex$ = this.selectedIndex.asObservable()
            .pipe(
                switchMap(idx => {
                    // Get the timer and add a little buffer (100ms) so that the selected value doesn't expire
                    // too soon and mess up the logic to skip items to catch up to our selected item
                    const timer = this.currentIndexSetup(idx, !isUndefined(idx), () => this.cancelDelay$.next()) + 100;

                    this.debugLog.log(`***** selectedIndex$`, idx, timer);

                    // From rxjs docs:
                    // "You can think of concat like a line at a ATM, the next transaction (subscription) cannot
                    // start until the previous completes!"
                    //
                    // So this will output the currently selected index, and then it will output a delay. this
                    // results in a broadcast of, "hey, this index was selected, now wait for a bit..."
                    return concat(
                        of(idx),
                        of(void 0).pipe(
                            delay(timer)
                        )
                    );
                })
            );

        // Combine the normal timer with the click handler (dots, prev/next arrows)
        return combineLatest([selectedIndex$, currentIndex$])
            .pipe(
                map(([sel, curr]) => {
                    // Store selected value
                    this.selectedIndexValue = sel;

                    // Get the current or selected index
                    const idx = this.getValidIndex(sel ?? curr);

                    this.debugLog.log(`***** carousel`, sel, curr, idx, Date.now());

                    // This needs to return an "iterable" value for the subscription to work
                    // It will return either the selected value (if applicable) or just the current index
                    // from the range loop.
                    return idx;
                })
            );
    }

    private currentIndexSetup(idx: number, condition: boolean, callback?: any) {
        this.debugLog.log(`***** currentIndexSetup - condition`, condition);

        let timer = 0;
        if (condition === true) {
            // Set the index
            this.currentIndex = idx;

            // Set css accordingly
            this.setActiveClass(this.currentIndex);

            // Set data for announcement for a11y
            this.slideAnnouncementData = {
                blockData: this.data?.blockData,
                currentItemLabel: this.labels?.currentItem,
                totalItems: this.items?.length,
            };

            // Get timer for index
            timer = this.getTimer(this.currentIndex);

            // If we have a callback execute it
            if (typeof callback === 'function') {
                this.debugLog.log(`***** currentIndexSetup - execute callback`, callback);
                callback();
            }
        }

        return timer;
    }

    private getTouch(document: Document) {
        const touchStart$ = fromEvent(document, 'touchstart').pipe(
            map((event: any) => event.touches[0])
        );

        const touchMove$ = (startEvent) => fromEvent(document, 'touchmove')
            .pipe(
                takeUntil(fromEvent(document, 'touchend')),

                // Get the coordinates of the touch
                map((event: any) => event.touches[0].pageX),

                // Determine the direction of the touch based on where it started and ended
                map((pageX: number) => Math.round(startEvent.pageX - pageX)),

                // Take the last value - this is where the touch ends
                takeLast(1),

                // Get +/- 1 - this will determine what the next index is
                map((pageX: number) => pageX > 50 || pageX < -50 ? Math.sign(pageX) : false)
            );

        return touchStart$
            .pipe(
                switchMap((startEvent: TouchEvent) => touchMove$(startEvent)),
                tap((e: number) => {
                    // Emit to cancel the range loop delay
                    this.cancelDelay$.next();

                    // Set active
                    this.setActive(this.currentIndex + e);
                })
            );
    }

    private setActiveClass(activeIndex: number) {
        // Get all the child components
        const carouselContent = this.el.nativeElement.querySelector('finder-carousel-content-renderer>ul');
        if (!carouselContent) {
            return;
        }

        Array.from(carouselContent.children).forEach((child, idx) => {
            // Remove the active class from the child component - doing this every time so we don't
            // wind up "doubling up" on the CSS class name
            this.renderer.removeClass(child, 'active');
            this.renderer.setAttribute(child, 'aria-hidden', 'true');

            // If this child index matches the current index add the `active` CSS class
            if (idx === activeIndex) {
                this.renderer.addClass(child, 'active');
                this.renderer.removeAttribute(child, 'aria-hidden');
            }
        });
    }

    // Get the CMS timer value or provide a fallback
    private getTimer(idx: number) {
        return get(this.data, `blockData[${idx}].slideTimer`, 5) * 1000;
    }

    // Get the CMS autoAdvance value
    private getAutoAdvance() {
        return get(this.data, `autoAdvance`, true);
    }

    // Make sure the selected index is within bounds
    private getValidIndex(idx: number) {
        if (idx < 0) {
            idx = this.itemCount - 1;
        } else if (idx >= this.itemCount) {
            idx = 0;
        }

        return idx;
    }
}
